import { Component } from "@theme/component";

/**
 * Size Picker Component
 * Handles shape toggling and localStorage persistence for fabric size filtering
 */
class SizePickerComponent extends Component {
  constructor() {
    super();
    this.localStorageKey = "fabricSizePickerState";
  }

  connectedCallback() {
    this.initializeShapeToggle();
    this.initializeDimensionSelects();
    this.restoreStateFromLocalStorage();

    // NOTE: restoreStateFromLocalStorage() already dispatches fabricSizeChange
    // at the end after setting all dimension values. No extra dispatch needed here.

    // Schedule a post-connect verification: on back-navigation (non-bfcache),
    // the browser may restore <select> values to what they were when the user
    // left the page. This happens AFTER connectedCallback returns, overwriting
    // the values we just set from localStorage. A delayed check catches this.
    setTimeout(() => {
      this._verifySelectValues();
    }, 50);

    // Handle bfcache (Back-Forward Cache) restoration.
    // When a user navigates back (e.g., product page → collection page),
    // the browser may serve the page from bfcache. In that case,
    // connectedCallback does NOT re-fire and the JS state is frozen
    // from when the user left. localStorage may have been updated on
    // the product page, so we need to re-read it.
    if (!this._pageshowBound) {
      this._pageshowHandler = (event) => {
        if (event.persisted) {
          // Use setTimeout(0) to run AFTER the browser's own bfcache form restoration.
          // Browsers may reset <select> values to their frozen state after pageshow fires,
          // which would overwrite our programmatic value changes. Deferring ensures we
          // apply localStorage values last.
          setTimeout(() => {
            this.restoreStateFromLocalStorage();
            // NOTE: restoreStateFromLocalStorage() already dispatches fabricSizeChange
            // at the end after setting all dimension values. No extra dispatch needed.
          }, 0);
        }
      };
      window.addEventListener('pageshow', this._pageshowHandler);
      this._pageshowBound = true;
    }
  }

  /**
   * Initialize shape toggle functionality
   */
  initializeShapeToggle() {
    const shapeInputs = this.querySelectorAll(".shape-input");

    shapeInputs.forEach((input) => {
      input.addEventListener("change", (e) => {
        const target = e.target;
        if (target instanceof HTMLInputElement) {
          this.handleShapeChange(target.value);
        }
      });
    });
  }

  /**
   * Initialize dimension select event listeners
   */
  initializeDimensionSelects() {
    const selects = this.querySelectorAll(".dimension-select");

    selects.forEach((select) => {
      select.addEventListener("change", () => {
        this.saveStateToLocalStorage();
        this.dispatchSizeChangeEvent();
      });
    });
  }

  /**
   * Handle shape change event
   * @param {string} selectedShape - The selected shape value
   */
  handleShapeChange(selectedShape) {
    // Update pill visual state
    const pills = this.querySelectorAll(".shape-pill");
    pills.forEach((pill) => {
      const pillShape = pill.getAttribute("data-shape");
      if (pillShape === selectedShape) {
        pill.classList.add("checked");
      } else {
        pill.classList.remove("checked");
      }
    });

    // Toggle dimension visibility
    const rectangularDimensions = this.querySelector(".rectangular-dimensions");
    const roundDimensions = this.querySelector(".round-dimensions");

    if (
      rectangularDimensions instanceof HTMLElement &&
      roundDimensions instanceof HTMLElement
    ) {
      if (selectedShape === "Rectangular") {
        rectangularDimensions.style.display = "";
        roundDimensions.style.display = "none";
      } else if (selectedShape === "Round") {
        rectangularDimensions.style.display = "none";
        roundDimensions.style.display = "";
      }
    }

    // During restoration, skip save & dispatch — dimension values aren't set yet.
    // The caller (restoreStateFromLocalStorage) will dispatch after all values are in place.
    if (this._isRestoring) {
      return;
    }

    // Save to localStorage
    this.saveStateToLocalStorage();

    // Dispatch size change event
    this.dispatchSizeChangeEvent();
  }

  /**
   * Calculate area in square feet based on current shape and dimensions
   */
  calculateArea() {
    const selectedShape = this.getSelectedShape();

    if (selectedShape === "Rectangular") {
      const widthFeetEl = this.querySelector('[name="width-feet"]');
      const widthInchesEl = this.querySelector('[name="width-inches"]');
      const lengthFeetEl = this.querySelector('[name="length-feet"]');
      const lengthInchesEl = this.querySelector('[name="length-inches"]');

      const widthFeet =
        widthFeetEl instanceof HTMLSelectElement
          ? parseFloat(widthFeetEl.value) || 0
          : 0;
      const widthInches =
        widthInchesEl instanceof HTMLSelectElement
          ? parseFloat(widthInchesEl.value) || 0
          : 0;
      const lengthFeet =
        lengthFeetEl instanceof HTMLSelectElement
          ? parseFloat(lengthFeetEl.value) || 0
          : 0;
      const lengthInches =
        lengthInchesEl instanceof HTMLSelectElement
          ? parseFloat(lengthInchesEl.value) || 0
          : 0;

      const widthTotal = widthFeet + widthInches / 12;
      const lengthTotal = lengthFeet + lengthInches / 12;

      return widthTotal * lengthTotal;
    } else if (selectedShape === "Round") {
      const diameterFeetEl = this.querySelector('[name="diameter-feet"]');
      const diameterInchesEl = this.querySelector('[name="diameter-inches"]');

      const diameterFeet =
        diameterFeetEl instanceof HTMLSelectElement
          ? parseFloat(diameterFeetEl.value) || 0
          : 0;
      const diameterInches =
        diameterInchesEl instanceof HTMLSelectElement
          ? parseFloat(diameterInchesEl.value) || 0
          : 0;

      const diameterTotal = diameterFeet + diameterInches / 12;
      const radius = diameterTotal / 2;

      return Math.PI * radius * radius;
    }

    return 0;
  }

  /**
   * Dispatch custom event when size changes
   */
  dispatchSizeChangeEvent() {
    const area = this.calculateArea();
    const shape = this.getSelectedShape();
    const dimensions = this.getDimensionValues();

    // Update validation UI
    this.updateValidation(area);

    const event = new CustomEvent("fabricSizeChange", {
      detail: {
        area: area,
        shape: shape,
        dimensions: dimensions,
      },
      bubbles: true,
    });

    this.dispatchEvent(event);
  }

  /**
   * Update validation error state based on area
   * @param {number} area - Calculated area in square feet
   */
  updateValidation(area) {
    const validationInfo = this.querySelector('[data-validation-error]');

    if (!validationInfo) return;

    // Add error class if area is greater than 0 but less than 12
    if (area > 0 && area < 12) {
      validationInfo.classList.add('has-error');
    } else {
      validationInfo.classList.remove('has-error');
    }
  }

  /**
   * Save current state to localStorage
   */
  saveStateToLocalStorage() {
    // Don't save during restoration to prevent overwriting with incomplete data
    if (this._isRestoring) {
      return;
    }

    const state = {
      shape: this.getSelectedShape(),
      dimensions: this.getDimensionValues(),
    };

    try {
      localStorage.setItem(this.localStorageKey, JSON.stringify(state));
    } catch (error) {
      console.warn("Failed to save size picker state to localStorage:", error);
    }
  }

  /**
   * Restore state from localStorage
   */
  restoreStateFromLocalStorage() {
    try {
      const savedState = localStorage.getItem(this.localStorageKey);

      if (!savedState) {
        return;
      }

      const state = JSON.parse(savedState);

      // Set flag to prevent saving during restoration
      this._isRestoring = true;

      // Restore shape selection
      if (state.shape) {
        const shapeInput = this.querySelector(
          `.shape-input[value="${state.shape}"]`,
        );
        if (shapeInput instanceof HTMLInputElement) {
          shapeInput.checked = true;
          this.handleShapeChange(state.shape);
        }
      }

      // Restore dimension values
      if (state.dimensions) {
        Object.entries(state.dimensions).forEach(([key, value]) => {
          const select = this.querySelector(`[name="${key}"]`);
          if (
            select instanceof HTMLSelectElement &&
            value !== null &&
            value !== undefined
          ) {
            select.value = value;
          }
        });
      }

      // Clear flag after restoration is complete
      this._isRestoring = false;

      // Dispatch size change event to trigger price calculations
      this.dispatchSizeChangeEvent();
    } catch (error) {
      console.warn(
        "Failed to restore size picker state from localStorage:",
        error,
      );
      this._isRestoring = false;
    }
  }

  /**
   * Get currently selected shape
   */
  getSelectedShape() {
    const checkedInput = this.querySelector(".shape-input:checked");
    if (checkedInput instanceof HTMLInputElement) {
      return checkedInput.value;
    }
    return null;
  }

  /**
   * Get all dimension select values
   */
  getDimensionValues() {
    const dimensions = {};
    const selects = this.querySelectorAll(".dimension-select");

    selects.forEach((select) => {
      if (select instanceof HTMLSelectElement) {
        dimensions[select.name] = select.value;
      }
    });

    return dimensions;
  }

  /**
   * Called by morph.js after the component is updated
   */
  updatedCallback() {
    super.updatedCallback();

    // Re-initialize after morph
    this.initializeShapeToggle();
    this.initializeDimensionSelects();

    // Restore state after morph
    this.restoreStateFromLocalStorage();

    // Schedule a post-morph verification: the morph engine may copy server-rendered
    // attribute values onto <select> elements AFTER updatedCallback returns,
    // overwriting the values we just set. Re-check against localStorage and
    // force-correct any selects that were reverted.
    requestAnimationFrame(() => {
      this._verifySelectValues();
    });
  }

  /**
   * Verifies that <select> values match localStorage and corrects them if not.
   * Handles the case where morph's attribute-copying overwrites values set
   * by restoreStateFromLocalStorage(), or where the browser's own form
   * restoration overwrites values after connectedCallback.
   */
  _verifySelectValues() {
    try {
      const savedState = localStorage.getItem(this.localStorageKey);
      if (!savedState) return;

      const state = JSON.parse(savedState);
      if (!state.dimensions) return;

      let corrected = false;
      Object.entries(state.dimensions).forEach(([key, value]) => {
        const select = this.querySelector(`[name="${key}"]`);
        if (
          select instanceof HTMLSelectElement &&
          value !== null &&
          value !== undefined
        ) {
          if (select.value !== String(value)) {
            select.value = value;
            corrected = true;
          }
        }
      });

      if (corrected) {
        this.dispatchSizeChangeEvent();
      }
    } catch (e) {
      console.warn("[SizePicker] Error verifying select values:", e);
    }
  }
}

if (!customElements.get("size-picker-component")) {
  customElements.define("size-picker-component", SizePickerComponent);
}
